'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Adult Diapers & Incontinence Products: A Senior Guide"
      author="MicroPlastics Editorial Team"
      date="March 19, 2026"
      readTime="9 min read"
      heroImage="/blog/app-search.png"
      heroImageAlt="Microplastics in adult diapers and incontinence products"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Adult diapers, incontinence pads, and protective underwear (Depend,
        Tena, Always Discreet, Prevail, etc.) are made of the same materials
        as baby diapers: <strong>polypropylene topsheet, polyethylene
        backsheet, and superabsorbent polymer (sodium polyacrylate) core</strong>.
        For someone using them 24/7 for years (Alzheimer&apos;s care,
        post-surgery recovery, severe incontinence), cumulative skin contact
        exceeds even an infant&apos;s diaper exposure. Safer options:
        <strong> washable cotton incontinence underwear (Conni, Wearever,
        Knix Leakproof), Eco by Naty Adult Pads, and product-specific
        plant-based brands</strong>. Cost savings over disposables can be
        significant.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Adult disposables use the same PP + PE + SAP construction as baby diapers.</>,
          <>For 24/7 users (dementia care, post-surgery, severe incontinence), cumulative skin contact is among the highest microplastic exposures in adults.</>,
          <>Washable cotton incontinence underwear (Conni, Wearever, Knix Leakproof) eliminates exposure and saves $1,500-4,000/year vs disposables.</>,
          <>Some brands now offer plant-based or PFAS-free options (Eco by Naty Adult, Honest Company Adult exploring).</>,
          <>For light incontinence: reusable absorbent underwear may fully replace disposable pads.</>,
          <>Skin care matters: switch to fragrance-free natural skincare to reduce barrier-disrupting chemicals.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why this matters for seniors
      </h2>
      <p>
        Approximately <strong>50% of women over 65 and 17% of men over 60</strong>
        experience some level of urinary incontinence. For those needing
        protection, products are worn 8-24 hours per day, often for years.
        Total plastic-skin contact in this scenario rivals or exceeds infancy.
      </p>
      <p>
        Compounding factors:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Senior skin is <strong>thinner and more fragile</strong> than younger adult skin, higher chemical absorption per gram of contact.</li>
        <li><strong>Reduced mobility</strong> means changing less frequently, extending plastic contact time.</li>
        <li><strong>Dementia care</strong> often involves nighttime + daytime continuous wear.</li>
        <li>Many brands include <strong>fragrances and lotions</strong> that add chemical exposure.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Adult disposable product materials
      </h2>
      <p>
        Almost identical to baby diapers:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Topsheet (skin contact)</strong>, polypropylene nonwoven.</li>
        <li><strong>Absorbent core</strong>, sodium polyacrylate SAP + fluff pulp.</li>
        <li><strong>Backsheet</strong>, polyethylene film.</li>
        <li><strong>Elastic + tabs</strong>, polyurethane, polyester, adhesives.</li>
        <li><strong>Fragrance / lotion (in some products)</strong>, synthetic perfume mixtures, often with phthalates.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Disposable vs reusable comparison
      </h2>

      <BlogComparisonTable
        caption="Adult incontinence products ranked"
        columns={[
          { key: 'product', label: 'Product / Brand' },
          { key: 'material', label: 'Material' },
          { key: 'cost', label: 'Cost per year (regular user)' },
        ]}
        rows={[
          { product: 'Washable cotton incontinence briefs (Conni Ladies/Mens, Wearever, ZorbieZ)', material: '100% cotton + waterproof PUL layer', cost: '$200-500 (one-time)' },
          { product: 'Knix Leakproof Underwear', material: 'Cotton + polyester blend with absorbent crotch', cost: '$200-400 (set of 5-10)' },
          { product: 'Reusable washable pads (Saalt, Eco-DK)', material: 'Cotton + bamboo + waterproof PUL', cost: '$80-150 (one-time)' },
          { product: 'Eco by Naty Adult Diapers (plant-based)', material: '~85% plant-based topsheet', cost: '$1,800-3,000/yr' },
          { product: 'Honest Company adult (some products)', material: 'Plant-based blends; verify per product', cost: '$1,500-2,800/yr' },
          { product: 'Depend (conventional)', material: 'PP + PE + SAP + fragrance', cost: '$1,400-2,500/yr' },
          { product: 'Tena Slim (conventional)', material: 'PP + PE + SAP', cost: '$1,500-2,800/yr' },
          { product: 'Always Discreet (conventional)', material: 'PP + PE + SAP + fragrance + lotion', cost: '$1,200-2,200/yr' },
          { product: 'Generic store brand', material: 'PP + PE + SAP', cost: '$700-1,400/yr' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Reusable washable underwear: the underused option
      </h2>
      <p>
        Reusable absorbent underwear has come a long way and is now a viable
        replacement for disposables for light-to-moderate incontinence:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Knix Leakproof Underwear (multiple absorbency levels)</strong>, popular, well-fitting, machine-washable. $20-40/pair.</li>
        <li><strong>Conni Ladies / Conni Men Active</strong>. Australian brand specifically for incontinence; high absorbency. $35-55/pair.</li>
        <li><strong>Wearever Reusable Incontinence Underwear</strong>, broad sizing including bariatric. $20-30/pair.</li>
        <li><strong>Modibodi Incontinence Range</strong>. Australian brand; multiple absorbency levels. $35-50/pair.</li>
        <li><strong>For overnight or higher-volume needs:</strong> reusable cloth diapers (similar construction to baby cloth diapers in adult sizes) from brands like Snap-EZ or All-In-Two Adult Cloth.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Skin care during incontinence
      </h2>
      <p>
        Chronic moisture and friction lead to incontinence-associated
        dermatitis (IAD). Common care products add additional chemical
        exposure. Cleaner choices:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Skip fragranced wipes.</strong> Plain water + a cotton washcloth is dramatically cleaner.</li>
        <li><strong>Use a barrier cream</strong>, zinc oxide based (Triple Paste, Calmoseptine) instead of petrolatum-heavy.</li>
        <li><strong>Choose EWG VERIFIED skin lotions</strong>. Earth Mama, ATTITUDE Adult Care.</li>
        <li><strong>Air-dry skin</strong> when possible during pad/diaper changes.</li>
        <li><strong>Change frequently</strong>, extended wear = higher chemical and microplastic exposure plus higher IAD risk.</li>
      </ul>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Cost comparison: reusable beats disposable
      </h2>
      <p>
        A starter kit of 6 reusable absorbent briefs at $30-40 each
        ($180-240) plus monthly laundry costs typically beats disposable
        annual spending of $1,500-3,000 within the first 3-6 months. For
        long-term care, reusable is dramatically cheaper.
      </p>
      <p>
        Combination approach: reusable for daytime + light overnight,
        disposable for highest-need overnight or travel. Cuts disposable use
        by 60-80%.
      </p>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-disposable-diapers" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in disposable diapers
        </Link>,{' '}
        <Link href="/blog/microplastics-in-wet-wipes-and-baby-wipes" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in wet wipes
        </Link>, and{' '}
        <Link href="/blog/microplastics-and-skin-absorption" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics and skin absorption
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Product packaging. PET, HDPE, PP, PS, PVC, multi-layer, glass, aluminum.</>,
          <>Container condition from photo, scratches, dents, fade.</>,
          <>Brand and product category, flags for known PFAS / BPA / fragranced lines.</>,
          <>Use-context flags, heat exposure, microwave, reuse cycles.</>,
          <>Cited research, every score links the specific studies behind it.</>,
        ]}
      />

      <BlogAppCTA
        headline="Translate the research into 5-second shelf decisions"
        sub="Reading the studies is step one. Acting on them at the grocery store is step two. The MicroPlastics app scores each product 0–100 using research like this."
        buttonLabel="Get the MicroPlastics app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Are adult diapers safe?',
            answer:
              'Conventional adult diapers (Depend, Tena, Always Discreet) use the same PP + PE + SAP construction as baby diapers. For continuous 24/7 wear over years, cumulative skin contact is meaningful. Washable cotton alternatives (Knix Leakproof, Conni, Wearever) eliminate exposure for light-moderate incontinence and save significantly on cost.',
          },
          {
            question: 'Do adult diapers contain PFAS?',
            answer:
              'Some brands have been independently tested and found to contain PFAS chemicals. Mamavation has tested some products. Eco by Naty Adult and Honest Company plant-based lines explicitly claim PFAS-free. Verify with current Mamavation testing for your specific brand.',
          },
          {
            question: 'What is the best washable incontinence underwear?',
            answer:
              'Knix Leakproof Underwear (well-fitting, multiple absorbency levels, $20-40/pair), Conni Ladies/Mens Active (Australian incontinence-specific, $35-55), Wearever (broad sizing), and Modibodi are top picks. All eliminate microplastic exposure and save $1,500-2,500/year compared to disposables.',
          },
          {
            question: 'Are reusable absorbent underwear enough for severe incontinence?',
            answer:
              'For severe incontinence (overnight high-volume, dementia care), reusable underwear may need to be combined with disposable backup or used during the day with disposable overnight. Many caregiver families use combination approaches that cut disposable use by 60-80%.',
          },
          {
            question: 'Are fragranced adult incontinence products bad?',
            answer:
              'Fragranced products contain synthetic perfume mixtures often including phthalates, endocrine disruptors. Always choose fragrance-free options. Brand lines explicitly fragrance-free: Tena ProSkin (fragrance-free version), some Depend Real Fit lines, Eco by Naty Adult.',
          },
          {
            question: 'How can I help an aging parent reduce microplastic exposure?',
            answer:
              'Switch to reusable absorbent underwear for daytime + light needs; use fragrance-free disposables only for high-volume situations. Use plain water + cotton washcloth instead of fragranced wipes. Choose EWG VERIFIED skincare. Change protection more frequently to reduce extended wear time.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'National Association For Continence',
            year: 2024,
            title: 'Urinary Incontinence Statistics and Demographics',
            publisher: 'NAFC',
            url: 'https://www.nafc.org/',
          },
          {
            authors: 'Mamavation',
            year: 2024,
            title: 'Disposable & Cloth Diapers Tested For Indications of PFAS',
            publisher: 'Mamavation',
            url: 'https://mamavation.com/motherhood/diapers-pfas-forever-chemicals.html',
          },
          {
            authors: 'Park JY, Yi BR, Lee KJ, et al.',
            year: 2025,
            title: 'Phthalate Exposure from Disposable Diapers',
            publisher: 'Toxics',
            url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC11946139/',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2023,
            title: 'Re-evaluation of bisphenol A (BPA)',
            publisher: 'EFSA Journal',
            url: 'https://www.efsa.europa.eu/en/efsajournal/pub/6857',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
